import { useEffect, useState } from 'react'
import { useAuthenticatedFetch } from '../lib/api'

// Live status indicator for a sidebar nav item.
//   null       — no pip
//   'live'     — neon, steady (default for current/just-updated)
//   'pending'  — warning/yellow pulsing (ongoing background task)
//   'alert'    — red pulsing (needs attention)
export type PipKind = null | 'live' | 'pending' | 'alert'

export interface NavStatus {
  studio: PipKind
  gallery: PipKind
  datasets: PipKind
  settings: PipKind
}

const POLL_INTERVAL_MS = 30_000

// Pulls a few signals in the background to drive the sidebar's live status
// pips. Kept deliberately cheap: two endpoints on mount, and a 30s poll of
// datasets only if any of them are currently training. Everything else is
// best-effort; failures silently yield null.
export default function useNavStatus(): NavStatus {
  const authenticatedFetch = useAuthenticatedFetch()
  const [datasetsPending, setDatasetsPending] = useState(false)
  const [lowCredits, setLowCredits] = useState(false)

  useEffect(() => {
    let cancelled = false
    let timer: ReturnType<typeof setInterval> | null = null

    const fetchDatasets = async () => {
      try {
        const res = await authenticatedFetch('/api/datasets')
        const { data } = (await res.json()) as {
          data: Array<{ lora_status: string }>
        }
        if (cancelled) return
        setDatasetsPending(data.some((d) => d.lora_status === 'training'))
      } catch {
        // ignore
      }
    }

    const fetchCredits = async () => {
      // Settings already exposes credits via Clerk's publicMetadata, but
      // we hit /api/billing/purchases just to confirm the API is reachable
      // AND derive a rough credit floor. For now we rely on a separate
      // lightweight "me" endpoint if it exists, else we skip. Low-credit
      // logic is handled client-side elsewhere.
      try {
        const res = await authenticatedFetch('/api/billing/purchases')
        if (!cancelled && !res.ok) return
      } catch {
        // ignore
      }
    }

    fetchDatasets()
    fetchCredits()
    // Only poll if at least one training is live — cheap when idle.
    timer = setInterval(() => {
      if (datasetsPending) fetchDatasets()
    }, POLL_INTERVAL_MS)

    return () => {
      cancelled = true
      if (timer) clearInterval(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticatedFetch])

  // Low-credit hint: the sidebar can read Clerk metadata separately via
  // useUser() where it's rendered, so here we just surface the network-
  // derived signals. The sidebar merges them with its own user check.
  void lowCredits
  void setLowCredits

  return {
    studio: null,
    gallery: null,
    datasets: datasetsPending ? 'pending' : null,
    settings: null,
  }
}
